import { settingsNotificationsWebPushSubscriptionPath } from "@/routes"

export interface WebPushPageProps {
  enabled: boolean
  vapid_public_key: string | null
}

interface WebPushServerRegistration {
  renewalToken: string
  renewalUrl: string
}

interface WebPushRegistrationResponse {
  data?: {
    renewal_token?: string
    renewal_url?: string
  }
}

const WEB_PUSH_CONFIG_DATABASE = "triyoga-web-push"
const WEB_PUSH_CONFIG_STORE = "configuration"
const WEB_PUSH_CONFIG_KEY = "renewal"

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = atob(base64)
  const output = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i)
  }

  return output
}

export function csrfToken(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? ""
  )
}

export function isIosLikeDevice(): boolean {
  if (typeof navigator === "undefined") return false

  const userAgent = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(userAgent)) return true

  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false

  const standaloneMedia = window.matchMedia(
    "(display-mode: standalone)",
  ).matches
  const overlayMedia = window.matchMedia(
    "(display-mode: window-controls-overlay)",
  ).matches
  const navigatorStandalone = Boolean(
    (navigator as Navigator & { standalone?: boolean }).standalone,
  )

  return standaloneMedia || overlayMedia || navigatorStandalone
}

export function webPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  )
}

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register("/service-worker")
}

export async function prepareWebPush(): Promise<ServiceWorkerRegistration> {
  if (!webPushSupported()) throw new Error("web_push_unsupported")

  await registerPushServiceWorker()
  return navigator.serviceWorker.ready
}

async function syncWebPushServiceWorkerConfig(
  vapidPublicKey: string,
  serverRegistration: WebPushServerRegistration,
): Promise<void> {
  const database = await openWebPushConfigDatabase()

  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(
        WEB_PUSH_CONFIG_STORE,
        "readwrite",
      )
      transaction.objectStore(WEB_PUSH_CONFIG_STORE).put(
        {
          vapidPublicKey,
          renewalToken: serverRegistration.renewalToken,
          renewalUrl: serverRegistration.renewalUrl,
        },
        WEB_PUSH_CONFIG_KEY,
      )
      transaction.oncomplete = () => resolve()
      transaction.onerror = () =>
        reject(
          transaction.error ??
            new Error("Could not save Web Push configuration"),
        )
      transaction.onabort = () =>
        reject(
          transaction.error ??
            new Error("Could not save Web Push configuration"),
        )
    })
  } finally {
    database.close()
  }
}

function openWebPushConfigDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(WEB_PUSH_CONFIG_DATABASE, 1)

    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(WEB_PUSH_CONFIG_STORE)) {
        database.createObjectStore(WEB_PUSH_CONFIG_STORE)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () =>
      reject(
        request.error ?? new Error("Could not open Web Push configuration"),
      )
  })
}

async function clearWebPushServiceWorkerConfig(): Promise<void> {
  const database = await openWebPushConfigDatabase()

  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(
        WEB_PUSH_CONFIG_STORE,
        "readwrite",
      )
      transaction.objectStore(WEB_PUSH_CONFIG_STORE).delete(WEB_PUSH_CONFIG_KEY)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () =>
        reject(
          transaction.error ??
            new Error("Could not clear Web Push configuration"),
        )
      transaction.onabort = () =>
        reject(
          transaction.error ??
            new Error("Could not clear Web Push configuration"),
        )
    })
  } finally {
    database.close()
  }
}

export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  if (!webPushSupported()) return null

  const registration =
    await navigator.serviceWorker.getRegistration("/service-worker")
  if (!registration) return null

  return registration.pushManager.getSubscription()
}

function subscriptionUsesVapidKey(
  subscription: PushSubscription,
  vapidPublicKey: string,
): boolean {
  const actualKey = subscription.options.applicationServerKey
  if (!actualKey) return true

  const actual = new Uint8Array(actualKey)
  const expected = urlBase64ToUint8Array(vapidPublicKey)

  return (
    actual.length === expected.length &&
    actual.every((value, index) => value === expected[index])
  )
}

async function discardSubscription(
  subscription: PushSubscription,
): Promise<void> {
  await clearWebPushServiceWorkerConfig().catch(() => undefined)
  await subscription.unsubscribe().catch(() => undefined)
  await deleteWebPushSubscription(subscription.endpoint).catch(() => undefined)
}

export async function reconcileWebPushSubscription(
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  const registration = await prepareWebPush()
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return null

  if (!subscriptionUsesVapidKey(subscription, vapidPublicKey)) {
    await discardSubscription(subscription)
    return null
  }

  let serverRegistration: WebPushServerRegistration
  try {
    serverRegistration = await persistWebPushSubscription(subscription)
    await syncWebPushServiceWorkerConfig(vapidPublicKey, serverRegistration)
  } catch (error) {
    await discardSubscription(subscription)
    throw error
  }

  return subscription
}

export async function requestWebPushPermission(): Promise<NotificationPermission> {
  return Notification.requestPermission()
}

export async function subscribeToWebPush(
  vapidPublicKey: string,
): Promise<PushSubscription> {
  const permission = await requestWebPushPermission()
  if (permission !== "granted") {
    const error = new Error(
      permission === "denied" ? "permission_denied" : "permission_dismissed",
    )
    error.name = "WebPushPermissionError"
    throw error
  }

  const registration = await prepareWebPush()
  let subscription = await registration.pushManager.getSubscription()

  if (subscription && !subscriptionUsesVapidKey(subscription, vapidPublicKey)) {
    await discardSubscription(subscription)
    subscription = null
  }

  subscription ??= await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
  })

  let serverRegistration: WebPushServerRegistration
  try {
    serverRegistration = await persistWebPushSubscription(subscription)
    await syncWebPushServiceWorkerConfig(vapidPublicKey, serverRegistration)
  } catch (error) {
    await discardSubscription(subscription)
    throw error
  }

  return subscription
}

export async function unsubscribeFromWebPush(): Promise<void> {
  const subscription = await getExistingPushSubscription()
  if (!subscription?.endpoint) {
    await clearWebPushServiceWorkerConfig()
    return
  }

  await deleteWebPushSubscription(subscription.endpoint)
  await clearWebPushServiceWorkerConfig()
  await subscription.unsubscribe()
}

export async function disconnectWebPushForLogout(): Promise<void> {
  const subscription = await getExistingPushSubscription().catch(() => null)
  await clearWebPushServiceWorkerConfig().catch(() => undefined)
  await subscription?.unsubscribe().catch(() => undefined)

  if (subscription?.endpoint) {
    void deleteWebPushSubscription(subscription.endpoint, true).catch(
      () => undefined,
    )
  }
}

async function persistWebPushSubscription(
  subscription: PushSubscription,
): Promise<WebPushServerRegistration> {
  const json = subscription.toJSON()
  const response = await fetch(settingsNotificationsWebPushSubscriptionPath(), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
    }),
  })

  if (!response.ok || response.redirected) {
    throw new Error("subscribe_failed")
  }

  const payload = (await response.json()) as WebPushRegistrationResponse
  const renewalToken = payload.data?.renewal_token
  const renewalUrl = payload.data?.renewal_url
  if (!renewalToken || !renewalUrl) throw new Error("subscribe_failed")

  return { renewalToken, renewalUrl }
}

async function deleteWebPushSubscription(
  endpoint: string,
  keepalive = false,
): Promise<void> {
  const response = await fetch(settingsNotificationsWebPushSubscriptionPath(), {
    method: "DELETE",
    credentials: "same-origin",
    keepalive,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ endpoint }),
  })

  if (!response.ok || response.redirected) {
    throw new Error("unsubscribe_failed")
  }
}
